<template>
  <div
    class="screen-recording-display"
    :class="[aspectRatioClass, { 'is-fullscreen': isFullscreen, 'is-playing': isPlaying }]"
    @mousemove="handleMouseMove"
    @click="togglePlay"
    @keydown.esc="exitFullscreen"
    tabindex="0"
  >
    <div v-if="documents.length > 0" class="documents-stack">
      <!-- Loop through all documents and display them -->
      <div
        v-for="(document, index) in documents"
        :key="index"
        class="document-item"
      >
        <div class="document-container">
          <!-- Document Preview -->
          <div class="document-preview">
            <!-- Show video if it's an MP4/MOV file -->
            <div class="media-container video-container" v-if="isVideoFile(document.type) && (document.preview || document.dataUrl)">
              <div class="media-content">
                <video
                  ref="videoElement"
                  autoplay
                  loop
                  muted
                  class="document-video"
                  :src="document.preview || document.dataUrl"
                  :alt="document.name"
                  @error="handleMediaError"
                ></video>
              </div>

              <!-- Add Live Transcript component -->
              <live-transcript
                v-show="isPlaying"
                :voice-speed="voiceSpeed"
                :text="activeSegmentContent"
                :speaker="activeSegmentSpeaker"
                :speaker-image="speakerImage"
                :visible="isPlaying"
                :message-id="messageId"
                :background-color="transcriptBackgroundColor"
              />
            </div>

            <!-- If it's a video but we DON'T have preview data, show name-based placeholder -->
            <div v-else-if="isVideoFile(document.type)" class="media-container video-placeholder">
              <div class="media-content">
                <div class="placeholder-icon">🎥</div>
                <div class="video-name">{{ document.name }}</div>
                <div class="video-info">Video preview not available</div>
              </div>

              <!-- Add Live Transcript component for placeholder too -->
              <live-transcript
                v-show="isPlaying"
                :voice-speed="voiceSpeed"
                :text="activeSegmentContent"
                :speaker="activeSegmentSpeaker"
                :speaker-image="speakerImage"
                :visible="isPlaying"
                :message-id="messageId"
                :background-color="transcriptBackgroundColor"
              />
            </div>

            <!-- Show image with animation if it's an image and we have preview data -->
            <div class="media-container image-container" v-else-if="isImageFile(document.type) && (document.preview || document.dataUrl)">
              <div class="media-content">
                <div class="image-animation-container" :class="[currentAnimation, noRepeatAnimation ? 'no-repeat' : '']">
                  <img
                    :src="document.preview || document.dataUrl"
                    :alt="document.name"
                    class="document-image"
                    @error="handleMediaError"
                  />
                </div>
              </div>

              <!-- Add Live Transcript component -->
              <live-transcript
                v-show="isPlaying"
                :voice-speed="voiceSpeed"
                :text="activeSegmentContent"
                :speaker="activeSegmentSpeaker"
                :speaker-image="speakerImage"
                :visible="isPlaying"
                :message-id="messageId"
                :background-color="transcriptBackgroundColor"
              />
            </div>

            <!-- If it's an image but we DON'T have preview data, show name-based placeholder -->
            <div v-else-if="isImageFile(document.type)" class="media-container image-placeholder">
              <div class="media-content">
                <div class="placeholder-icon">🖼️</div>
                <div class="image-name">{{ document.name }}</div>
                <div class="image-info">Image preview not available</div>
              </div>

              <!-- Add Live Transcript component for placeholder too -->
              <live-transcript
                v-show="isPlaying"
                :voice-speed="voiceSpeed"
                :text="activeSegmentContent"
                :speaker="activeSegmentSpeaker"
                :speaker-image="speakerImage"
                :visible="isPlaying"
                :message-id="messageId"
                :background-color="transcriptBackgroundColor"
              />
            </div>

            <!-- Non-image, non-video document (PDF, DOCX, etc) -->
            <div v-else class="media-container file-icon-container">
              <div class="media-content">
                <div class="file-icon" :class="getFileIconClass(document.type)">
                  {{ getFileExtension(document.name).toUpperCase() }}
                </div>
                <div class="document-name">{{ document.name }}</div>
              </div>

              <!-- Add Live Transcript component for document files -->
              <live-transcript
                v-show="isPlaying"
                :voice-speed="voiceSpeed"
                :text="activeSegmentContent"
                :speaker="activeSegmentSpeaker"
                :speaker-image="speakerImage"
                :visible="isPlaying"
                :message-id="messageId"
                :background-color="transcriptBackgroundColor"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No documents placeholder -->
    <div v-else class="media-container no-documents">
      <div class="media-content">
        <div class="placeholder-icon">📄</div>
        <p>No documents available</p>
      </div>

      <!-- Add Live Transcript component for empty state -->
      <live-transcript
        v-show="isPlaying"
        :voice-speed="voiceSpeed"
        :text="activeSegmentContent"
        :speaker="activeSegmentSpeaker"
        :speaker-image="speakerImage"
        :visible="isPlaying"
        :message-id="messageId"
        :background-color="transcriptBackgroundColor"
      />
    </div>

    <!-- UI Controls Layer - Only visible before playing or when manually shown -->
    <div class="ui-controls-layer" :class="{ 'hidden': isPlaying && !showControls }">
      <!-- Large play button (only visible when paused) -->
      <div v-if="!isPlaying" class="large-play-button-container">
        <button class="nav-button prev-button" @click.stop="skipPrevious" aria-label="Previous segment">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polygon points="19 20 9 12 19 4 19 20"></polygon>
            <line x1="5" y1="19" x2="5" y2="5"></line>
          </svg>
        </button>
        <button class="large-play-button" aria-label="Play">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polygon points="5 3 19 12 5 21 5 3"></polygon>
          </svg>
        </button>
        <button class="nav-button next-button" @click.stop="skipNext" aria-label="Next segment">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polygon points="5 4 15 12 5 20 5 4"></polygon>
            <line x1="19" y1="5" x2="19" y2="19"></line>
          </svg>
        </button>
      </div>

      <!-- Minimalist fullscreen toggle - Special design for screen recording -->
      <button
        @click.stop="toggleFullscreen"
        class="fullscreen-toggle"
        :class="{ 'recording-mode': recordingMode }"
        aria-label="Toggle fullscreen"
      >
        <svg v-if="!isFullscreen" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M8 3H5a2 2 0 0 0-2 2v3"></path>
          <path d="M21 8V5a2 2 0 0 0-2-2h-3"></path>
          <path d="M3 16v3a2 2 0 0 0 2 2h3"></path>
          <path d="M16 21h3a2 2 0 0 0 2-2v-3"></path>
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M8 3v3a2 2 0 0 1-2 2H3"></path>
          <path d="M21 8h-3a2 2 0 0 1-2-2V3"></path>
          <path d="M3 16h3a2 2 0 0 1 2 2v3"></path>
          <path d="M16 21v-3a2 2 0 0 1 2-2h3"></path>
        </svg>
      </button>

      <!-- Only show aspect ratio and animation options when not playing -->
      <div v-if="!isPlaying" class="recording-settings">
        <!-- Settings toggle button -->
        <button @click.stop="toggleSettings" class="settings-toggle" aria-label="Toggle settings">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="3"></circle>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
          </svg>
        </button>

        <!-- Settings panel (conditionally shown) -->
        <div v-if="showSettingsPanel" class="settings-panel" @click.stop>
          <!-- Recording mode toggle -->
          <div class="settings-section">
            <label class="settings-label">
              <input type="checkbox" v-model="recordingMode" @change="saveSettings">
              <span>Recording Mode</span>
              <span class="settings-hint">(Hides all UI elements during playback)</span>
            </label>
          </div>

          <!-- Aspect Ratio -->
          <div class="settings-section">
            <label>Aspect Ratio:</label>
            <div class="setting-buttons">
              <button
                v-for="ratio in availableAspectRatios"
                :key="ratio.value"
                @click.stop="setAspectRatio(ratio.value)"
                :class="{ active: aspectRatio === ratio.value }"
                class="setting-button"
              >
                {{ ratio.label }}
              </button>
            </div>
          </div>

          <!-- Animation style selector (only for images) -->
          <div v-if="hasImages" class="settings-section">
            <label>Animation Style:</label>
            <div class="setting-buttons">
              <button
                v-for="anim in availableAnimations"
                :key="anim.value"
                @click.stop="setAnimationStyle(anim.value)"
                :class="{ active: animationStyle === anim.value }"
                class="setting-button"
              >
                {{ anim.label }}
              </button>
            </div>

            <!-- Animation options -->
            <div class="animation-options">
              <label class="settings-checkbox">
                <input type="checkbox" v-model="holdAnimationAtEnd" @change="saveSettings">
                <span>Hold at end position</span>
              </label>

              <label class="settings-checkbox">
                <input type="checkbox" v-model="synchronizeWithSpeaking" @change="saveSettings">
                <span>Match speaking duration</span>
              </label>

              <label v-if="!holdAnimationAtEnd && !synchronizeWithSpeaking" class="settings-checkbox">
                <input type="checkbox" v-model="noRepeatAnimation" @change="saveSettings">
                <span>Don't repeat animation</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Keyboard shortcut overlay - Press '?' to toggle -->
    <div v-if="showKeyboardShortcuts" class="keyboard-shortcuts-overlay" @click.stop="showKeyboardShortcuts = false">
      <div class="keyboard-shortcuts-panel" @click.stop>
        <h3>Keyboard Shortcuts</h3>
        <div class="shortcut-list">
          <div class="shortcut-item">
            <span class="key">Space</span>
            <span>Play/Pause</span>
          </div>
          <div class="shortcut-item">
            <span class="key">F</span>
            <span>Toggle Fullscreen</span>
          </div>
          <div class="shortcut-item">
            <span class="key">Esc</span>
            <span>Exit Fullscreen</span>
          </div>
          <div class="shortcut-item">
            <span class="key">←</span>
            <span>Previous Segment</span>
          </div>
          <div class="shortcut-item">
            <span class="key">→</span>
            <span>Next Segment</span>
          </div>
          <div class="shortcut-item">
            <span class="key">R</span>
            <span>Toggle Recording Mode</span>
          </div>
          <div class="shortcut-item">
            <span class="key">?</span>
            <span>Show/Hide Shortcuts</span>
          </div>
        </div>
        <button @click.stop="showKeyboardShortcuts = false" class="close-button">Close</button>
      </div>
    </div>
  </div>
</template>

<script>
import LiveTranscript from '../components/LiveTranscript.vue';

export default {
  name: 'ScreenRecordingDisplay',
  components: {
    LiveTranscript
  },
  props: {
    messageId: {
      required: true,
    },
    voiceSpeed: {
      type: Number,
      default: 1
    },
    speakerImage: {
      type: String,
      default: '/static/Gemini_Generated_Image_uobiifuobiifuobi.jpeg'
    },
    documents: {
      type: Array,
      default: () => []
    },
    isPlaying: {
      type: Boolean,
      default: false
    },
    activeSegmentContent: {
      type: String,
      default: ''
    },
    activeSegmentSpeaker: {
      type: String,
      default: ''
    },
    transcriptBackgroundColor: {
      type: String,
      default: 'rgba(0, 0, 0, 0.6)'
    }
  },
  data() {
    return {
      // UI Controls
      showControls: false,
      controlsTimer: null,
      showSettingsPanel: false,
      recordingMode: true,  // Default to recording mode for screen capture
      showKeyboardShortcuts: false,

      // Animation options
      availableAnimations: [
        { value: 'random', label: 'Random' },
        { value: 'zoom-pan', label: 'Zoom & Pan' },
        { value: 'ken-burns', label: 'Ken Burns' },
        { value: 'fade-scale', label: 'Fade & Scale' },
        { value: 'gentle-float', label: 'Gentle Float' },
        { value: 'smooth-slide', label: 'Smooth Slide' }
      ],
      availableAspectRatios: [
        { value: '16:9', label: '16:9' },
        { value: '4:3', label: '4:3' },
        { value: '1:1', label: '1:1' },
        { value: '9:16', label: '9:16' }
      ],
      animations: [
        'zoom-pan-effect',
        'ken-burns-effect',
        'fade-and-scale',
        'gentle-float',
        'smooth-slide'
      ],
      animationIndex: 0,
      animationStyle: 'zoom-pan',
      aspectRatio: '16:9',
      isFullscreen: false,
      animationTimer: null,
      currentAnimation: 'zoom-pan-effect',
      noRepeatAnimation: false,
      animationDuration: 30,
      animationStartTime: 0,
      previousSegmentContent: '',
      holdAnimationAtEnd: true,
      synchronizeWithSpeaking: true,
      segmentInProgress: false,

      // Transcript settings
      transcriptOpacity: 0.7,

      // Last interaction timestamp for inactivity detection
      lastInteractionTime: Date.now()
    };
  },
  computed: {
    // Determine if there are images to animate
    hasImages() {
      return this.documents.some(doc => this.isImageFile(doc.type));
    },
    // Get current aspect ratio class for CSS
    aspectRatioClass() {
      switch (this.aspectRatio) {
        case '16:9': return 'aspect-16-9';
        case '4:3': return 'aspect-4-3';
        case '1:1': return 'aspect-1-1';
        case '9:16': return 'aspect-9-16';
        default: return 'aspect-16-9';
      }
    }
  },
  watch: {
    // Watch for transcript background color and opacity changes
    transcriptOpacity: {
      handler() {
        this.updateTranscriptBackground();
      }
    },
    // Watch for isPlaying changes to update UI
    isPlaying(newVal, oldVal) {
      if (newVal) {
        // Started playing
        this.handlePlayStart();
      } else {
        // Stopped playing
        this.handlePlayStop();
      }
    },
    // Watch for segment content changes to handle animations
    activeSegmentContent(newContent, oldContent) {
      if (newContent !== oldContent && newContent !== '') {
        // Save previous content for comparison
        this.previousSegmentContent = oldContent;
        this.segmentInProgress = true;

        // Handle animation style changes
        if (this.animationStyle === 'random' && this.isPlaying) {
          this.changeRandomAnimation();
        }

        // Handle animation timing
        if (this.synchronizeWithSpeaking && this.isPlaying) {
          this.restartAnimations();
          this.animationStartTime = Date.now();
        }
      } else if (newContent === '' && oldContent !== '') {
        this.segmentInProgress = false;
      }
    },
    // Watch recording mode changes
    recordingMode(newVal) {
      // If entering recording mode, hide controls
      if (newVal && this.isPlaying) {
        this.showControls = false;
      }
    }
  },
  mounted() {
    this.loadSettings();
    this.startAnimationCycle();
    this.addEventListeners();
    this.updateTranscriptBackground();
  },
  beforeDestroy() {
    this.removeEventListeners();

    if (this.animationTimer) {
      clearInterval(this.animationTimer);
    }

    if (this.controlsTimer) {
      clearTimeout(this.controlsTimer);
    }
  },
  methods: {
    // ---- Event Handling ----
    addEventListeners() {
      // Add fullscreen change event listener
      document.addEventListener('fullscreenchange', this.handleFullscreenChange);
      document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);

      // Add keyboard event listeners
      window.addEventListener('keydown', this.handleKeyDown);

      // Mouse movement tracking
      this.$el.addEventListener('mousemove', this.handleMouseMove);
    },

    removeEventListeners() {
      document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
      window.removeEventListener('keydown', this.handleKeyDown);
      this.$el.removeEventListener('mousemove', this.handleMouseMove);
    },

    handleKeyDown(e) {
      // Only handle if this component is focused
      if (document.activeElement !== this.$el) return;

      switch (e.key) {
        case ' ': // Space - Play/Pause
          e.preventDefault();
          this.togglePlay();
          break;
        case 'f': // F - Fullscreen
        case 'F':
          e.preventDefault();
          this.toggleFullscreen();
          break;
        case 'Escape': // Escape - Exit fullscreen
          if (this.isFullscreen) {
            this.exitFullscreen();
          }
          break;
        case 'ArrowLeft': // Left arrow - Previous
          e.preventDefault();
          this.skipPrevious();
          break;
        case 'ArrowRight': // Right arrow - Next
          e.preventDefault();
          this.skipNext();
          break;
        case '?': // ? - Show keyboard shortcuts
          e.preventDefault();
          this.showKeyboardShortcuts = !this.showKeyboardShortcuts;
          break;
        case 'r': // R - Toggle recording mode
        case 'R':
          e.preventDefault();
          this.recordingMode = !this.recordingMode;
          this.saveSettings();
          break;
      }
    },

    handleMouseMove() {
      // Update last interaction time
      this.lastInteractionTime = Date.now();

      // Show controls if not in recording mode or if not playing
      if (!this.recordingMode || !this.isPlaying) {
        this.showControlsTemporarily();
      }
    },

    // Handle play start event
    handlePlayStart() {
      this.segmentInProgress = true;
      this.animationStartTime = Date.now();
      this.restartAnimations();

      // If in recording mode, hide controls after short delay
      if (this.recordingMode) {
          this.showControls = false;
      }
    },

    // Handle play stop event
    handlePlayStop() {
      this.segmentInProgress = false;

      // Always show controls when stopped
      this.showControls = true;
    },

    // ---- UI Control Methods ----
    togglePlay() {
      this.$emit('toggle-play');
    },

    skipPrevious() {
      this.$emit('skip-previous');
    },

    skipNext() {
      this.$emit('skip-next');
    },

    toggleSettings() {
      this.showSettingsPanel = !this.showSettingsPanel;
    },

    // Show controls temporarily
    showControlsTemporarily() {
      // Clear existing timer
      if (this.controlsTimer) {
        clearTimeout(this.controlsTimer);
      }

      // Show controls
      this.showControls = true;

      // Hide after delay if playing in recording mode
      if (this.isPlaying && this.recordingMode) {
        this.controlsTimer = setTimeout(() => {
          this.showControls = false;
        }, 2500);
      }
    },

    // ---- Animation Methods ----
    changeRandomAnimation() {
      let newIndex;
      do {
        newIndex = Math.floor(Math.random() * this.animations.length);
      } while (newIndex === this.animationIndex && this.animations.length > 1);

      this.animationIndex = newIndex;

      const animContainer = this.$el.querySelector('.image-animation-container');
      if (animContainer) {
        animContainer.classList.add('animation-transition');

        this.animations.forEach(anim => {
          animContainer.classList.remove(anim);
        });

        void animContainer.offsetWidth;

        this.currentAnimation = this.animations[this.animationIndex];
        animContainer.classList.add(this.currentAnimation);

        setTimeout(() => {
          animContainer.classList.remove('animation-transition');
        }, 1000);
      }
    },

    restartAnimations() {
      const animContainer = this.$el.querySelector('.image-animation-container');
      if (animContainer) {
        if (this.segmentInProgress) {
          animContainer.classList.add('animation-transition');
        }

        this.animations.forEach(anim => {
          animContainer.classList.remove(anim);
        });

        void animContainer.offsetWidth;

        animContainer.classList.add(this.currentAnimation);

        if (this.holdAnimationAtEnd || this.synchronizeWithSpeaking) {
          // Estimate reading time based on content length
          const wordCount = this.activeSegmentContent.split(/\s+/).length;
          const estimatedSeconds = Math.max(10, Math.min(60, (wordCount / 150) * 60));

          animContainer.style.animationDuration = `${estimatedSeconds}s`;
          this.animationDuration = estimatedSeconds;
        } else {
          animContainer.style.animationDuration = '';
        }

        setTimeout(() => {
          animContainer.classList.remove('animation-transition');
        }, 1000);
      }
    },

    startAnimationCycle() {
      if (this.animationTimer) {
        clearInterval(this.animationTimer);
        this.animationTimer = null;
      }

      if (this.animationStyle === 'random' && !this.holdAnimationAtEnd && !this.synchronizeWithSpeaking) {
        this.currentAnimation = this.animations[this.animationIndex];

        if (!this.noRepeatAnimation) {
          this.animationTimer = setInterval(() => {
            if (!this.segmentInProgress || !this.synchronizeWithSpeaking) {
              this.changeRandomAnimation();
            }
          }, 15000);
        }
      } else {
        const animMap = {
          'zoom-pan': 'zoom-pan-effect',
          'ken-burns': 'ken-burns-effect',
          'fade-scale': 'fade-and-scale',
          'gentle-float': 'gentle-float',
          'smooth-slide': 'smooth-slide'
        };
        this.currentAnimation = animMap[this.animationStyle] || 'zoom-pan-effect';
      }

      this.$nextTick(() => {
        this.restartAnimations();
      });
    },

    // ---- File Type Detection Methods ----
    isImageFile(fileType) {
      return fileType && fileType.startsWith('image/');
    },

    isVideoFile(fileType) {
      return fileType && (
        fileType.startsWith('video/') ||
        fileType.includes('mp4') ||
        fileType.includes('mov') ||
        fileType.includes('quicktime') ||
        fileType.includes('webm') ||
        fileType.includes('ogg')
      );
    },

    getFileExtension(filename) {
      if (!filename) return '';
      return filename.split('.').pop() || '';
    },

    getFileIconClass(fileType) {
      if (!fileType) return 'generic-icon';
      if (fileType.includes('pdf')) return 'pdf-icon';
      if (fileType.includes('word') || fileType.includes('doc')) return 'doc-icon';
      if (fileType.includes('excel') || fileType.includes('sheet') || fileType.includes('xls')) return 'xls-icon';
      if (fileType.includes('powerpoint') || fileType.includes('presentation') || fileType.includes('ppt')) return 'ppt-icon';
      if (this.isVideoFile(fileType)) return 'video-icon';
      return 'generic-icon';
    },

    handleMediaError(e) {
      console.warn('Media load error:', e.target.alt);
      e.target.style.display = 'none';

      const placeholder = document.createElement('div');
      placeholder.className = 'file-icon generic-icon';
      placeholder.textContent = this.getFileExtension(e.target.alt).toUpperCase();
      e.target.parentNode.appendChild(placeholder);

      const errorMsg = document.createElement('div');
      errorMsg.className = 'media-error-message';
      errorMsg.textContent = `${e.target.tagName === 'VIDEO' ? 'Video' : 'Image'} could not be loaded`;
      e.target.parentNode.appendChild(errorMsg);
    },

    // ---- Settings Methods ----
    setAnimationStyle(style) {
      this.animationStyle = style;
      this.startAnimationCycle();
      this.saveSettings();

      if (this.isPlaying) {
        this.restartAnimations();
      }
    },

    setAspectRatio(ratio) {
      this.aspectRatio = ratio;
      this.saveSettings();
    },

    // Update transcript background based on opacity
    updateTranscriptBackground() {
      // Create new background color with updated opacity
      const rgba = `rgba(0, 0, 0, ${this.transcriptOpacity})`;
      this.$emit('update:transcriptBackgroundColor', rgba);
      this.saveSettings();
    },

    // ---- Fullscreen Methods ----
    toggleFullscreen() {
      if (!this.isFullscreen) {
        this.enterFullscreen();
      } else {
        this.exitFullscreen();
      }

      // Show controls temporarily
      this.showControlsTemporarily();
    },

    enterFullscreen() {
      const element = this.$el;

      try {
        if (element.requestFullscreen) {
          element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          element.webkitRequestFullscreen();
        } else if (element.msRequestFullscreen) {
          element.msRequestFullscreen();
        }

        this.isFullscreen = true;
        document.body.classList.add('has-fullscreen-element');

        // Lock orientation to landscape if possible
        if (screen.orientation && screen.orientation.lock) {
          screen.orientation.lock('landscape').catch(e => {
            console.log('Orientation lock not supported');
          });
        }

        // Add event listeners for resizing
        window.addEventListener('resize', this.handleFullscreenResize);
        window.addEventListener('orientationchange', this.handleFullscreenResize);

        // Force immediate resize handling
        this.handleFullscreenResize();
      } catch (e) {
        console.error('Fullscreen API error:', e);
        // Fallback for devices without proper fullscreen support
        this.isFullscreen = true;
        document.body.classList.add('has-fullscreen-element');
      }
    },

    exitFullscreen() {
      try {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }

        // Unlock orientation
        if (screen.orientation && screen.orientation.unlock) {
          screen.orientation.unlock();
        }

        // Remove event listeners
        window.removeEventListener('resize', this.handleFullscreenResize);
        window.removeEventListener('orientationchange', this.handleFullscreenResize);

        document.body.classList.remove('has-fullscreen-element');
      } catch (e) {
        console.error('Exit fullscreen error:', e);
      }

      this.isFullscreen = false;
    },

    handleFullscreenResize() {
      if (this.isFullscreen) {
        this.$nextTick(() => {
          const element = this.$el;
          if (element) {
            // Force reflow for mobile browsers
            element.style.display = 'none';
            const forceRepaint = element.offsetHeight;
            element.style.display = '';

            setTimeout(() => {
              // Force hardware acceleration
              element.style.transform = 'translateZ(0)';

              // Ensure content fills the screen
              const viewportHeight = window.innerHeight;
              if (element.offsetHeight < viewportHeight) {
                element.style.height = `${viewportHeight}px`;
              }
            }, 50);
          }
        });
      }
    },

    handleFullscreenChange() {
      const isInFullscreen = !!(document.fullscreenElement ||
                                document.webkitFullscreenElement ||
                                document.msFullscreenElement);

      this.isFullscreen = isInFullscreen;

      if (isInFullscreen) {
        document.body.classList.add('has-fullscreen-element');
      } else {
        document.body.classList.remove('has-fullscreen-element');
      }
    },

    // ---- Settings Storage Methods ----
    saveSettings() {
      localStorage.setItem(`recording_settings_${this.messageId}`, JSON.stringify({
        aspectRatio: this.aspectRatio,
        animationStyle: this.animationStyle,
        noRepeatAnimation: this.noRepeatAnimation,
        holdAnimationAtEnd: this.holdAnimationAtEnd,
        synchronizeWithSpeaking: this.synchronizeWithSpeaking,
        recordingMode: this.recordingMode,
        transcriptOpacity: this.transcriptOpacity
      }));
    },

    loadSettings() {
      const savedSettings = localStorage.getItem(`recording_settings_${this.messageId}`);
      if (savedSettings) {
        try {
          const settings = JSON.parse(savedSettings);
          if (settings.aspectRatio) {
            this.aspectRatio = settings.aspectRatio;
          }
          if (settings.animationStyle) {
            this.animationStyle = settings.animationStyle;
          }
          if (settings.noRepeatAnimation !== undefined) {
            this.noRepeatAnimation = settings.noRepeatAnimation;
          }
          if (settings.holdAnimationAtEnd !== undefined) {
            this.holdAnimationAtEnd = settings.holdAnimationAtEnd;
          }
          if (settings.synchronizeWithSpeaking !== undefined) {
            this.synchronizeWithSpeaking = settings.synchronizeWithSpeaking;
          }
          if (settings.recordingMode !== undefined) {
            this.recordingMode = settings.recordingMode;
          }
          if (settings.transcriptOpacity !== undefined) {
            this.transcriptOpacity = settings.transcriptOpacity;
          }
        } catch (e) {
          console.error('Error loading saved settings:', e);
        }
      }
    }
  }
};

</script>
<style scoped>
.screen-recording-display {
  width: 100%;
  position: relative;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  overflow: hidden;
  outline: none; /* Remove outline when focused */
}

/* Aspect ratio classes */
.aspect-16-9 {
  padding-top: 56.25%; /* 16:9 Aspect Ratio */
}

.aspect-4-3 {
  padding-top: 75%; /* 4:3 Aspect Ratio */
}

.aspect-1-1 {
  padding-top: 100%; /* 1:1 Square Aspect Ratio */
}

.aspect-9-16 {
  padding-top: 177.78%; /* 9:16 Vertical Aspect Ratio */
}

/* Fullscreen mode */
.screen-recording-display.is-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  padding-top: 0 !important;
  border-radius: 0;
  z-index: 9999;
  background-color: black;
  transition: none; /* Remove transitions in fullscreen for clean recording */
}

/* Document containers */
.documents-stack {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.document-item {
  position: relative;
  width: 100%;
  height: 100%;
}

.document-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
}

.document-preview {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* Media container */
.media-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.media-content {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* Image and video elements */
.document-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.document-video {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

/* UI Controls Layer - main container for UI elements */
.ui-controls-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  pointer-events: none; /* Let clicks pass through by default */
  transition: opacity 0.5s ease;
}

/* Hide controls in recording mode while playing */
.ui-controls-layer.hidden {
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.5s ease, visibility 0s 0.5s;
}

/* Make buttons clickable */
.ui-controls-layer button {
  pointer-events: auto;
}

/* Minimalist fullscreen toggle optimized for recording */
.fullscreen-toggle {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(28, 28, 28, 0.7);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;
  opacity: 0.7;
}

.fullscreen-toggle:hover {
  background: rgba(60, 60, 60, 0.9);
  transform: scale(1.05);
  opacity: 1;
}

/* In recording mode, make the fullscreen toggle more discreet */
.fullscreen-toggle.recording-mode {
  width: 36px;
  height: 36px;
  opacity: 0.6;
  background: rgba(0, 0, 0, 0.5);
}

/* Settings toggle */
.settings-toggle {
  position: absolute;
  top: -25px;
  left: 12px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(28, 28, 28, 0.7);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;
}

.settings-toggle:hover {
  background: rgba(60, 60, 60, 0.9);
  transform: scale(1.05);
}

/* Settings panel */
.recording-settings {
  position: absolute;
  bottom: 20px;
  left: 20px;
  z-index: 15;
}

.settings-panel {
  background: rgba(28, 28, 28, 0.85);
  border-radius: 8px;
  padding: 16px;
  width: 320px;
  color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  pointer-events: auto;
}

.settings-section {
  margin-bottom: 16px;
}

.settings-section:last-child {
  margin-bottom: 0;
}

.settings-section label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
}

.settings-hint {
  opacity: 0.7;
  font-size: 0.85em;
  font-weight: normal;
  margin-left: 4px;
}

.setting-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.setting-button {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  padding: 6px 10px;
  font-size: 13px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.setting-button:hover {
  background: rgba(255, 255, 255, 0.2);
}

.setting-button.active {
  background: rgba(66, 153, 225, 0.6);
}

/* Animation options */
.animation-options {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.settings-checkbox {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.settings-checkbox input[type="checkbox"] {
  margin-right: 8px;
  width: 16px;
  height: 16px;
}

/* Color and opacity controls */
.color-opacity-controls {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}

.color-opacity-controls label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
}

.color-opacity-controls input[type="range"] {
  flex: 1;
}

/* Large play button */
.large-play-button-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: auto;
}

.large-play-button {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(45, 29, 74, 0.8);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: transform 0.2s ease, background-color 0.2s ease;
  margin: 0 1rem;
}

.large-play-button:hover {
  transform: scale(1.1);
  background: rgba(66, 153, 225, 0.9);
}

.large-play-button svg {
  margin-left: 4px;
}

.nav-button {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(45, 29, 74, 0.8);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: transform 0.2s ease, background-color 0.2s ease;
}

.nav-button:hover {
  transform: scale(1.1);
  background: rgba(66, 153, 225, 0.9);
}

/* Keyboard shortcuts overlay */
.keyboard-shortcuts-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
  pointer-events: auto;
}

.keyboard-shortcuts-panel {
  background: rgba(40, 40, 40, 0.95);
  border-radius: 8px;
  padding: 24px;
  width: 360px;
  max-width: 90vw;
  color: white;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}

.keyboard-shortcuts-panel h3 {
  text-align: center;
  margin-top: 0;
  margin-bottom: 16px;
  font-size: 18px;
}

.shortcut-list {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 12px 16px;
  margin-bottom: 20px;
}

.shortcut-item {
  display: contents;
}

.key {
  display: inline-block;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  padding: 4px 8px;
  min-width: 24px;
  text-align: center;
  font-family: monospace;
  font-weight: bold;
}

.close-button {
  display: block;
  margin: 0 auto;
  background: rgba(66, 153, 225, 0.6);
  border: none;
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.close-button:hover {
  background: rgba(66, 153, 225, 0.8);
}

/* Image animation styles */
.image-animation-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Animation transitions */
.animation-transition {
  transition: transform 1s ease-in-out, opacity 1s ease-in-out;
}

/* Non-repeating animations */
.image-animation-container.no-repeat,
.holdAnimationAtEnd .image-animation-container,
.synchronizeWithSpeaking .image-animation-container {
  animation-iteration-count: 1 !important;
  animation-fill-mode: forwards !important;
}

/* Animation keyframes */
.zoom-pan-effect {
  animation: zoomPan 15s ease-in-out infinite;
}

.ken-burns-effect {
  animation: kenBurns 20s ease-in-out infinite;
}

.fade-and-scale {
  animation: fadeScale 12s ease-in-out infinite;
}

.gentle-float {
  animation: gentleFloat 10s ease-in-out infinite;
}

.smooth-slide {
  animation: smoothSlide 18s ease-in-out infinite;
}

@keyframes zoomPan {
  0%, 100% {
    transform: scale(1) translate(0, 0);
  }
  50% {
    transform: scale(1.1) translate(-2%, -1%);
  }
}

@keyframes kenBurns {
  0%, 100% {
    transform: scale(1.05) translate(-1%, -1%);
  }
  25% {
    transform: scale(1.09) translate(0%, 1%);
  }
  50% {
    transform: scale(1.12) translate(1%, 1%);
  }
  75% {
    transform: scale(1.09) translate(0%, 0%);
  }
}

@keyframes fadeScale {
  0%, 100% {
    opacity: 0.95;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.08);
  }
}

@keyframes gentleFloat {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes smoothSlide {
  0%, 100% {
    transform: translateX(-2%) scale(1.05);
  }
  50% {
    transform: translateX(2%) scale(1.05);
  }
}

/* File icon styles */
.file-icon {
  width: 120px;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 2rem;
  color: white;
  position: relative;
  margin-bottom: 16px;
}

.file-icon:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #e0e0e0;
  clip-path: polygon(0% 0%, 80% 0%, 100% 20%, 100% 100%, 0% 100%);
}

.file-icon:after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 20px;
  height: 20px;
  background: #c0c0c0;
  clip-path: polygon(0% 0%, 100% 0%, 100% 100%);
}

.pdf-icon:before { background: #FF5722; }
.doc-icon:before { background: #2196F3; }
.xls-icon:before { background: #4CAF50; }
.ppt-icon:before { background: #FF9800; }
.video-icon:before { background: #E91E63; }
.generic-icon:before { background: #9E9E9E; }

.document-name {
  font-size: 1rem;
  text-align: center;
  max-width: 300px;
  word-break: break-word;
}

/* Placeholder styles */
.image-placeholder, .video-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 1rem;
}

.placeholder-icon {
  font-size: 3rem;
  margin-bottom: 0.5rem;
  opacity: 0.7;
}

.image-name, .video-name {
  font-weight: 500;
  text-align: center;
  margin-bottom: 0.5rem;
  max-width: 100%;
  word-break: break-word;
}

.image-info, .video-info {
  font-size: 0.875rem;
  color: #999;
  text-align: center;
}

.media-error-message {
  color: #ff6b6b;
  font-size: 0.75rem;
  text-align: center;
  margin-top: 0.5rem;
}

/* No documents placeholder */
.no-documents {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #999;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* Mobile optimization */
@media (max-width: 768px) {
  .settings-panel {
    width: 280px;
    padding: 12px;
  }

  .large-play-button {
    width: 60px;
    height: 60px;
  }

  .large-play-button svg {
    width: 36px;
    height: 36px;
  }

  .nav-button {
    width: 40px;
    height: 40px;
  }

  .file-icon {
    width: 80px;
    height: 100px;
    font-size: 1.5rem;
  }

  .fullscreen-toggle,
  .settings-toggle {
    width: 36px;
    height: 36px;
  }
}

/* iOS Safari fixes for fullscreen */
@supports (-webkit-touch-callout: none) {
  .screen-recording-display.is-fullscreen {
    /* iOS Safari specific fixes */
    top: env(safe-area-inset-top, 0);
    bottom: env(safe-area-inset-bottom, 0);
    left: env(safe-area-inset-left, 0);
    right: env(safe-area-inset-right, 0);
    height: -webkit-fill-available;
  }
}

/* Additional styles for recording mode */
.screen-recording-display.is-playing.is-fullscreen .fullscreen-toggle {
  opacity: 0;
  transition: opacity 0.8s ease 1s;
}

.screen-recording-display.is-playing.is-fullscreen:hover .fullscreen-toggle {
  opacity: 0.6;
  transition: opacity 0.3s ease;
}

/* Enhanced recording mode styles */
.screen-recording-display.is-playing.is-fullscreen .ui-controls-layer {
  pointer-events: none;
}

.screen-recording-display.is-playing.is-fullscreen .ui-controls-layer.hidden .fullscreen-toggle {
  pointer-events: auto;
}

/* Complete hiding of all elements during recording except on hover */
.screen-recording-display.is-playing.is-fullscreen .ui-controls-layer.hidden * {
  opacity: 0;
  transition: opacity 0.8s ease;
}

.screen-recording-display.is-playing.is-fullscreen:hover .ui-controls-layer.hidden .fullscreen-toggle {
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

/* Ensure transcript is visible even when controls are hidden */
.screen-recording-display.is-playing.is-fullscreen .ui-controls-layer.hidden + .live-transcript {
  opacity: 1;
}

/* Print mode - if we ever need to hide all UI for screenshots */
.screen-recording-display.print-mode * {
  opacity: 0 !important;
  transition: none !important;
}

.screen-recording-display.print-mode .document-item,
.screen-recording-display.print-mode .document-image,
.screen-recording-display.print-mode .document-video,
.screen-recording-display.print-mode .live-transcript {
  opacity: 1 !important;
}

/* Custom focus styles for accessibility */
.screen-recording-display:focus {
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.5);
  outline: none;
}

/* Screen capture optimizations - prevent flickering in recordings */
.screen-recording-display.is-fullscreen {
  backface-visibility: hidden; /* Prevent flickering */
  transform: translateZ(0); /* Force hardware acceleration */
  will-change: transform; /* Hint to browser for optimization */
}

/* Ensure smooth animation playback in fullscreen */
.screen-recording-display.is-fullscreen .image-animation-container {
  backface-visibility: hidden;
  transform: translateZ(0);
  will-change: transform;
}

/* Prevent flickering in fullscreen */
.screen-recording-display.is-fullscreen .document-item {
  backface-visibility: hidden;
  transform: translateZ(0);
  will-change: transform;
}

</style>
